import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, LifeBuoy, Bug, MessageSquarePlus, Loader2, CheckCircle2 } from "lucide-react";

const CATEGORIES = [
  { value: "bug", label: "Report a bug", icon: Bug },
  { value: "feedback", label: "Share feedback", icon: MessageSquarePlus },
  { value: "other", label: "Something else", icon: LifeBuoy },
];

const statusStyle = (s) =>
  s === "resolved" ? "bg-[#ccff0c]/15 text-[#ccff0c] border-[#ccff0c]/30" : "bg-secondary text-muted-foreground border-border";

export default function Support() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ category: "bug", subject: "", message: "" });
  const [saving, setSaving] = useState(false);
  const [tickets, setTickets] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/support/mine").then(({ data }) => setTickets(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.subject.trim() || !form.message.trim()) { toast.error("Add a subject and a message"); return; }
    setSaving(true);
    try {
      await api.post("/support/ticket", form);
      toast.success("Thanks! Your message was sent to the team.");
      setForm({ category: "bug", subject: "", message: "" });
      load();
    } catch (e) { toast.error("Could not send. Try again."); }
    finally { setSaving(false); }
  };

  return (
    <AppShell title="Support & Feedback">
      <button onClick={() => navigate(-1)} className="flex items-center text-muted-foreground text-sm mb-3"><ChevronLeft className="h-4 w-4" /> Back</button>

      <Card className="rounded-2xl bg-card border-border p-4 space-y-3" data-testid="support-form">
        <p className="text-muted-foreground text-sm">Found a bug or have an idea? Tell us — the Real Deal team reads every message.</p>
        <div>
          <Label className="text-xs text-muted-foreground mb-1.5 block">Type</Label>
          <Select value={form.category} onValueChange={(v) => set("category", v)}>
            <SelectTrigger data-testid="support-category" className="bg-card border-border h-11"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover border-border text-foreground">
              {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1.5 block">Subject</Label>
          <Input data-testid="support-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Short summary" className="bg-card border-border h-11" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground mb-1.5 block">Message</Label>
          <Textarea data-testid="support-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Describe what happened or what you'd like to see…" className="bg-card border-border min-h-[120px]" />
        </div>
        <Button data-testid="support-submit" onClick={submit} disabled={saving} className="w-full h-12 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-semibold">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send to the team"}
        </Button>
      </Card>

      {tickets.length > 0 && (
        <div className="mt-5" data-testid="support-history">
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">Your messages</p>
          <div className="space-y-2 pb-24">
            {tickets.map((t) => (
              <Card key={t.id} data-testid={`support-ticket-${t.id}`} className="rounded-xl bg-card border-border p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-sm truncate">{t.subject}</p>
                  <Badge className={`border text-[10px] capitalize flex items-center gap-1 ${statusStyle(t.status)}`}>
                    {t.status === "resolved" && <CheckCircle2 className="h-3 w-3" />}{t.status}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{t.message}</p>
                <p className="text-[10px] text-muted-foreground mt-1.5 capitalize">{t.category} · {new Date(t.created_at).toLocaleDateString()}</p>
              </Card>
            ))}
          </div>
        </div>
      )}
    </AppShell>
  );
}
